# Route: /admin/my-tenant/profile
# F46 — Tenant admin self-service page to edit the organization profile.
# Platform admins read-only the same data via the tenant-detail page;
# this page is the write surface (PUT /api/my-tenant/profile).

page-admin-my-tenant-profile:
  type: Page
  archetype: edit
  meta:
    title: $t.admin.tenants.profile_page_title
    auth: required
  data:
    profile:
      model: TenantProfileResponse
      refresh: on_mount
    form:
      model: TenantProfileForm
      # Seed the form from the already-fetched profile so the editor shows the
      # tenant's real values (display name included) instead of a blank form.
      # Each $data reference falls back to "" at runtime if the field is absent.
      initial:
        displayName: $data.profile.displayName
        siteCode: $data.profile.siteCode
        primaryContact:
          name: $data.profile.primaryContact.name
          email: $data.profile.primaryContact.email
          phone: $data.profile.primaryContact.phone
        technicalContact:
          name: $data.profile.technicalContact.name
          email: $data.profile.technicalContact.email
          phone: $data.profile.technicalContact.phone
        address:
          line1: $data.profile.address.line1
          line2: $data.profile.address.line2
          city: $data.profile.address.city
          stateRegion: $data.profile.address.stateRegion
          postalCode: $data.profile.address.postalCode
          countryCode: $data.profile.address.countryCode
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - my-tenant-profile-header
    - my-tenant-profile-identity
    - my-tenant-profile-primary-contact
    - my-tenant-profile-technical-contact
    - my-tenant-profile-address
    - my-tenant-profile-actions

# ── Header ───────────────────────────────────────────────────────────────────

my-tenant-profile-header:
  type: PageHeader
  title: $t.admin.tenants.profile_page_title
  back_route: /today
  # F78c — read-only view of which features are enabled for this tenant
  # (GET /api/my-tenant/config). Editing requires a system administrator, at
  # /admin/tenants/:tenantId/features.
  actions:
    - label: $t.admin.tenants.view_features
      route: /admin/my-tenant/features
      color: info
      # Read-only navigation is tertiary (design-system/components.md), even as the only action.
      variant: ghost

# ── Identity ─────────────────────────────────────────────────────────────────

my-tenant-profile-identity:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - my-tenant-profile-identity-title
    - input-profile-display-name
    - input-profile-site-code

my-tenant-profile-identity-title:
  type: Text
  content: $t.admin.tenants.profile_section_identity
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface

input-profile-display-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_display_name
  bind: form.displayName
  a11y:
    label: $t.admin.tenants.profile_display_name_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-site-code:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_site_code
  bind: form.siteCode
  a11y:
    label: $t.admin.tenants.profile_site_code_label
  validation:
    # Mirrors TenantIdentity: ^[A-Za-z0-9-]+$ with MaxSiteCodeLength = 50. The
    # hyphen is escaped because the browser compiles `pattern` with the `v` flag,
    # where an unescaped `-` inside [...] is a syntax error and the whole pattern
    # is ignored (XP85043 refuses it at lint).
    pattern: '^[A-Za-z0-9\-]{1,50}$'
    messages:
      pattern: $t.admin.tenants.profile_site_code_invalid
  spatial:
    min_height: $size.touch-target

# ── Primary contact ──────────────────────────────────────────────────────────

my-tenant-profile-primary-contact:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - my-tenant-profile-primary-title
    - input-profile-primary-name
    - input-profile-primary-email
    - input-profile-primary-phone

my-tenant-profile-primary-title:
  type: Text
  content: $t.admin.tenants.profile_section_primary_contact
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface

input-profile-primary-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_contact_name
  bind: form.primaryContact.name
  a11y:
    label: $t.admin.tenants.profile_primary_name_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-primary-email:
  type: Input
  input_type: email
  label: $t.admin.tenants.profile_contact_email
  bind: form.primaryContact.email
  a11y:
    label: $t.admin.tenants.profile_primary_email_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-primary-phone:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_contact_phone
  bind: form.primaryContact.phone
  a11y:
    label: $t.admin.tenants.profile_primary_phone_label
  spatial:
    min_height: $size.touch-target

# ── Technical contact (optional) ─────────────────────────────────────────────

my-tenant-profile-technical-contact:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - my-tenant-profile-technical-title
    - input-profile-tech-name
    - input-profile-tech-email
    - input-profile-tech-phone

my-tenant-profile-technical-title:
  type: Text
  content: $t.admin.tenants.profile_section_technical_contact
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface

input-profile-tech-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_contact_name
  bind: form.technicalContact.name
  a11y:
    label: $t.admin.tenants.profile_tech_name_label
  spatial:
    min_height: $size.touch-target

input-profile-tech-email:
  type: Input
  input_type: email
  label: $t.admin.tenants.profile_contact_email
  bind: form.technicalContact.email
  a11y:
    label: $t.admin.tenants.profile_tech_email_label
  spatial:
    min_height: $size.touch-target

input-profile-tech-phone:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_contact_phone
  bind: form.technicalContact.phone
  a11y:
    label: $t.admin.tenants.profile_tech_phone_label
  spatial:
    min_height: $size.touch-target

# ── Address ──────────────────────────────────────────────────────────────────

my-tenant-profile-address:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - my-tenant-profile-address-title
    - input-profile-address-line1
    - input-profile-address-line2
    - input-profile-address-city
    - input-profile-address-state
    - input-profile-address-postal
    - input-profile-address-country

my-tenant-profile-address-title:
  type: Text
  content: $t.admin.tenants.profile_section_address
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface

input-profile-address-line1:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_line1
  bind: form.address.line1
  a11y:
    label: $t.admin.tenants.profile_address_line1_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-address-line2:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_line2
  bind: form.address.line2
  a11y:
    label: $t.admin.tenants.profile_address_line2_label
  spatial:
    min_height: $size.touch-target

input-profile-address-city:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_city
  bind: form.address.city
  a11y:
    label: $t.admin.tenants.profile_address_city_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-address-state:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_state
  bind: form.address.stateRegion
  a11y:
    label: $t.admin.tenants.profile_address_state_label
  spatial:
    min_height: $size.touch-target

input-profile-address-postal:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_postal
  bind: form.address.postalCode
  a11y:
    label: $t.admin.tenants.profile_address_postal_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

input-profile-address-country:
  type: Input
  input_type: text
  label: $t.admin.tenants.profile_address_country
  bind: form.address.countryCode
  a11y:
    label: $t.admin.tenants.profile_address_country_label
  validation:
    required: true
    pattern: "^[A-Z]{2}$"
    messages:
      pattern: $t.admin.tenants.profile_address_country_invalid
      invalid: $t.admin.tenants.profile_address_country_invalid
  spatial:
    min_height: $size.touch-target

# ── Actions ──────────────────────────────────────────────────────────────────
# The shared FormActions primitive owns button hierarchy, order, touch sizing,
# and sticky responsive placement. Its action is the profile's direct-fetch
# contract; the transform forwards it to the normal shared action emitter.

my-tenant-profile-actions:
  type: FormActions
  submit_label: $t.admin.tenants.profile_save
  cancel_route: /today
  # Profile Cancel is a product-level exit to tenant home, independent of the
  # navigation path used to open the editor.
  cancel_history: false
  action:
    type: custom
    method: PUT
    url: /api/my-tenant/profile
    body: form
    on_success: navigate(/today)
